<template>
    <div
        v-if="data.postId"
        class="preview-card-short"
        :class="[
            `preview-card-short--theme--${theme}`,
            {'preview-card-short--small': isSmall,},
            {'preview-card-short--excerpt-present': isSmall,}
        ]"
    >
        <div class="preview-card-short__inner">
            <div class="preview-card-short__content-area">
                <nuxt-link :to="localePath(postLink)" :aria-label="data.title">
                    <h2
                        class="preview-card-short__title"
                        :title="translatePost(data, 'title')"
                    >
                        {{ truncate(translatePost(data, 'title'), 35) }}
                    </h2>
                    <p v-if="isExcerptPresent" class="preview-card-short__excerpt">
                        {{ truncate(translatePost(data, 'excerpt'), excerptSymbolsCount) }}
                    </p>
                </nuxt-link>
            </div>
            <div v-show="isBottomAreaShown" class="preview-card-short__bottom-area">
                <span
                    v-if="-dateOfPublication > 24"
                    class="publish-date"
                >
                    {{ $moment(data.createdAt).locale($i18n.locale).format('MMMM DD, YYYY') }}
                </span>
                <span v-else class="publish-date">
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        class="publish-date__clock-icon"
                    >
                        <path
                            d="M8 3.5C8 3.36739 7.94732 3.24021 7.85355 3.14645C7.75979 3.05268 7.63261 3 7.5 3C7.36739 3 7.24021 3.05268 7.14645 3.14645C7.05268 3.24021 7 3.36739 7 3.5V9C7.00003 9.08813 7.02335 9.17469 7.06761 9.25091C7.11186 9.32712 7.17547 9.39029 7.252 9.434L10.752 11.434C10.8669 11.4961 11.0014 11.5108 11.127 11.4749C11.2525 11.4391 11.3591 11.3556 11.4238 11.2422C11.4886 11.1288 11.5065 10.9946 11.4736 10.8683C11.4408 10.7419 11.3598 10.6334 11.248 10.566L8 8.71V3.5Z"
                            fill="#ED4C28"/>
                        <path
                            d="M8 16C10.1217 16 12.1566 15.1571 13.6569 13.6569C15.1571 12.1566 16 10.1217 16 8C16 5.87827 15.1571 3.84344 13.6569 2.34315C12.1566 0.842855 10.1217 0 8 0C5.87827 0 3.84344 0.842855 2.34315 2.34315C0.842855 3.84344 0 5.87827 0 8C0 10.1217 0.842855 12.1566 2.34315 13.6569C3.84344 15.1571 5.87827 16 8 16ZM15 8C15 9.85652 14.2625 11.637 12.9497 12.9497C11.637 14.2625 9.85652 15 8 15C6.14348 15 4.36301 14.2625 3.05025 12.9497C1.7375 11.637 1 9.85652 1 8C1 6.14348 1.7375 4.36301 3.05025 3.05025C4.36301 1.7375 6.14348 1 8 1C9.85652 1 11.637 1.7375 12.9497 3.05025C14.2625 4.36301 15 6.14348 15 8Z"
                            fill="#ED4C28"/>
                    </svg>
                    <span>{{ $moment(data.createdAt).locale($i18n.locale).fromNow() }}</span>
                </span>
            </div>
        </div>

        <div
            class="preview-card-short__img-area"
            :class="{'preview-card-short__img-area--hidden': hasImage}"
        >
            <div
                v-if="data.isHasVideo && !isSmall"
                class="preview-card-short__badge--video"
            >
                <svg width="18" height="12" viewBox="0 0 18 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M14 4.5V1C14 0.734784 13.8946 0.48043 13.7071 0.292893C13.5196 0.105357 13.2652 0 13 0H1C0.734784 0 0.48043 0.105357 0.292893 0.292893C0.105357 0.48043 0 0.734784 0 1V11C0 11.2652 0.105357 11.5196 0.292893 11.7071C0.48043 11.8946 0.734784 12 1 12H13C13.2652 12 13.5196 11.8946 13.7071 11.7071C13.8946 11.5196 14 11.2652 14 11V7.5L18 11.5V0.5L14 4.5Z"
                        fill="white"/>
                </svg>
            </div>

            <div
                v-if="data.articleType && !isSmall"
                class="preview-card-short__badge"
                :class="{'second-badge-in-row': data.isHasVideo}"
            >
                {{ getArticleTypeName(data.articleType) }}
            </div>

            <PreviewCardBadge :badgeData="data.ageRestrictions"/>

            <nuxt-link :to="localePath(postLink)" :aria-label="data.title">
                <image-area
                    v-bind="imageArea"
                    :src="data.thumbnailImg.url"
                    :alt="data.thumbnailImg.alt"
                />
            </nuxt-link>
        </div>
    </div>
</template>

<script>
import utils from '@/mixins/utils'
import windowWidth from '@/mixins/windowWidth'
import PreviewCardBadge from './PreviewCardBadge'
import translatePostResolver from '@/mixins/translatePostResolver'
import articleTypeNameResolver from '@/mixins/articleTypeNameResolver'
import ImageArea from '@/components/UIComponents/ImageArea.vue'

export default {
    name: 'PreviewCardShort',
    mixins: [ utils, windowWidth, translatePostResolver, articleTypeNameResolver ],
    props: {
        data: {
            type: Object,
            required: true,
            default: () => ({
                title: '',
                thumbnailImg: {
                    url: '',
                    alt: ''
                },
                excerpt: '',
                createdAt: null,
                category: {
                    name: '',
                    slug: ''
                },
                postType: '',
                postId: null,
                slug: ''
            })
        },
        theme: {
            type: String,
            default: 'dark'
        },
        isSmall: {
            type: Boolean,
            default: false
        },
        isExcerptPresent: {
            type: Boolean,
            default: true
        },
        titleSymbolsCount: {
            type: Number,
            default: 51
        },
        excerptSymbolsCount: {
            type: [ Number, String ],
            default: 192
        },
        isBottomAreaShown: {
            type: Boolean,
            default: true
        },
        imageArea: {
            type: Object,
            default: function() {
                return {}
            }
        }
    },
    components: {
        ImageArea,
        PreviewCardBadge
    },
    data() {
        return {
            deviceWindowWidth: null
        }
    },
    mounted() {
        this.deviceWindowWidth = window.innerWidth
    },
    computed: {
        title() {
            if (this.isSmall) {
                return this.truncate(this.data.title, 40)
            }
            return this.truncate(this.data.title, this.titleSymbolsCount)
        },
        excerpt() {
            return this.truncate(this.data.excerpt, this.excerptSymbolsCount)
        },
        dateOfPublication() {
            return this.$moment(this.data.createdAt).diff(Date.now(), 'hours')
        },
        hasImage() {
            return (this.mixinIsPhone(this.deviceWindowWidth) || this.mixinIsTablet(this.deviceWindowWidth)) && !this.isSmall

        }
    }
}
</script>

<style lang="scss" scoped>
@import "~assets/css/_vars.scss";
@import "~assets/css/_extension.scss";

.preview-card-short {
    width: 100%;
    overflow: hidden;
    display: flex;
    border-radius: $cardsBorderRadius;

    &--theme--dark {
        .preview-card-short__title,
        .preview-card-short__excerpt {
            color: $mainLightFontColor;
        }

        .preview-card-short__bottom-area {
            color: $additionalInfoLightenColor;
        }

        &.preview-card-short {
            background-color: $accentDarkBg;

            &-transparent-bg {
                background-color: transparent;

                .preview-card-short__inner {
                    padding-left: 0;
                }
            }

            &-small {
                .preview-card-short__title {
                    color: #C1C6D0;
                }

                .preview-card-short__title {
                    color: rgba(#C1C6D0, 0.7);
                }
            }
        }
    }

    &--theme--dark:hover {
        .preview-card-short__title,
        .preview-card-short__excerpt {
            color: rgba($mainLightFontColor, 0.7);
            transition: $transitionAllFast;
        }
    }

    &--theme--light {
        .preview-card-short__title,
        .preview-card-short__excerpt {
            color: #000000;
        }

        .preview-card-short__bottom-area {
            color: $additionalInfoLightColor;
        }

        &.preview-card-short {
            background-color: $accentLightBg;

            &-transparent-bg {
                background-color: transparent;

                .preview-card-short__inner {
                    padding-left: 0;
                }
            }

            &-small {
                .preview-card-short__title {
                    color: #3E3E3E;
                }

                .preview-card-short__title:hover {
                    color: rgba(#3E3E3E, 0.7);
                }
            }
        }
    }

    &--theme--light:hover {
        .preview-card-short__title,
        .preview-card-short__excerpt {
            color: rgba(#000000, 0.7);
        }
    }

    &__img-area {
        position: relative;
        width: 40%;
        margin-left: auto;
        transition: $transitionAllFast;

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }

        &--hidden {
            display: none;
        }
    }

    &__badge {
        position: absolute;
        top: 5px;
        left: 5px;
        background-color: $accentColor;
        color: $mainLightFontColor;
        font-weight: 600;
        font-size: $smallTextFontSize;
        line-height: 17px;
        padding: 6px 10px;
        text-transform: capitalize;
    }

    &__badge--video {
        @extend %flexCenter;
        position: absolute;
        top: 0;
        left: 0;
        width: 40px;
        height: 40px;
        background-color: rgba(0, 0, 0, 0.8);
    }

    .second-badge-in-row {
        left: 50px;
    }

    &__inner {
        overflow: hidden;
        padding: 25px 20px 25px 30px;
        width: 60%;
    }

    &__title {
        font-weight: 600;
        font-size: 20px;
        line-height: 24px;
        text-transform: uppercase;
        transition: $transitionAllFast;
    }

    &__excerpt {
        font-size: $mainFontSize;
        line-height: 140%;
        margin-top: 15px;
        margin-bottom: 15px;
        transition: $transitionAllFast;
    }

    &__bottom-area {
        display: flex;
        align-items: center;

        .publish-date {
            margin-right: 20px;

            &__clock-icon {
                margin-right: 7px;
            }

            span {
                display: inline-block;
                vertical-align: top;
            }
        }

        .publish-date::first-letter {
            text-transform: uppercase;
        }
    }

    &--small {
        // height: 87px;
        font-size: $smallTextFontSize;
        line-height: 17px;

        .preview-card-short__title {
            text-transform: initial;
            font-size: $mainFontSize;
            font-weight: bold;
            line-height: 17px;
            margin-bottom: 10px;
        }

        .preview-card-short__inner {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            overflow: hidden;
            padding: 13px 20px;
        }
    }
}

@media (max-width: $breakpoint-tablet) {
    .preview-card-short {
        &__inner {
            width: 100%;
            padding: 18px 15px;
        }

        &__title {
            font-size: $bigTextFontSize;
            line-height: 22px;
        }
    }
}

@media (max-width: $breakpoint-phone) {
    .preview-card-short {
        &__inner {
            width: 100%;
            padding: 18px 15px;
        }

        &__title {
            font-size: $bigTextFontSize;
            line-height: 22px;
        }

        &__excerpt {
            font-size: $smallTextFontSize;
        }
    }
}
</style>
